import type { InboxItem } from "@/types/domain";

export interface InboxPrdGroup {
  kind: "prd";
  key: string;
  prdId: string;
  prd: InboxItem["parent"];
  engagements: InboxItem[];
  todos: InboxPrdGroup[];
}

type InboxGroup = InboxPrdGroup | { kind: "item"; key: string; item: InboxItem };

/** One document heading, with each App's pending work underneath it. Todo
 * documents nest under their parent, even when that parent has no pending work.
 * Groups follow their earliest pending item; grouping never creates a task. */
export function groupInbox(items: InboxItem[]): InboxGroup[] {
  const byPrd = new Map<string, InboxPrdGroup>();
  const parentIds = new Map<string, string>();
  const getGroup = (prdId: string): InboxPrdGroup => {
    let group = byPrd.get(prdId);
    if (!group) {
      group = { kind: "prd", key: `prd:${prdId}`, prdId, prd: null, engagements: [], todos: [] };
      byPrd.set(prdId, group);
    }
    return group;
  };

  for (const item of items) {
    if (item.kind !== "engagement" || !item.prdId) continue;
    const group = getGroup(item.prdId);
    group.prd = { id: item.prdId, shortId: group.engagements[0]?.shortId ?? item.shortId, docName: item.title, type: item.issueType ?? "prd", pullRequests: item.pullRequests, todoCounts: item.todoCounts, execStage: item.execStage };
    group.engagements.push(item);
    if (item.issueType === "todo" && item.parentId) {
      const parent = getGroup(item.parentId);
      parent.prd ??= item.parent;
      parentIds.set(item.prdId, item.parentId);
    }
  }

  const parents = new Map<InboxPrdGroup, InboxPrdGroup>();
  for (const [prdId, parentId] of parentIds) {
    const group = getGroup(prdId);
    const parent = getGroup(parentId);
    let ancestor = parent;
    while (ancestor !== group && parents.has(ancestor)) ancestor = parents.get(ancestor)!;
    // Legacy malformed parent links must not hide work or create a render loop.
    if (ancestor !== group) parents.set(group, parent);
  }

  const groups: InboxGroup[] = [];
  const added = new Set<InboxPrdGroup>();
  for (const item of items) {
    let group = item.kind === "engagement" && item.prdId ? byPrd.get(item.prdId) : undefined;
    if (!group) {
      groups.push({ kind: "item", key: `${item.kind}:${item.id}`, item });
      continue;
    }
    // Walking from the earliest item also orders nested todo groups by their
    // earliest pending descendant, without splitting a document across blocks.
    while (!added.has(group)) {
      added.add(group);
      const parent = parents.get(group);
      if (!parent) {
        groups.push(group);
        break;
      }
      parent.todos.push(group);
      group = parent;
    }
  }
  return groups;
}
